import RetailerLayout from "@faire/prototype-playground/components/RetailerLayout";
import { CompassProvider } from "@faire/prototype-playground/contexts/CompassContext";
import { LiveToolbar } from "@faire/prototype-playground/platform/components/toolbar/LiveToolbar";
import type { PrototypeMetadata } from "@faire/prototype-playground/workspace/types";
import { Button } from "@faire/slate/components/button";
import { useEffect, useState } from "react";

import { ControlOfferModal } from "./ControlOfferModal";
import { IncentiveTakeover } from "./IncentiveTakeover";
import { RetailerHomepage } from "./RetailerHomepage";
import { TermsModal } from "./TermsModal";

export const prototypeMeta: PrototypeMetadata = {
  name: "Instacart-style incentive banner",
  owner: "Vikas Lalwani",
  email: "vikas.lalwani@faire.com",
  status: "in-progress",
  platform: "web",
  createdAt: "2026-08-05",
  lastUpdated: "2026-08-05",
  surfaceArea: "Retailer",
  category: "experimental",
  creationTool: "claude-code",
  thumbnail: "/images/thumbnails/vikas-instacart-incentive-banner.png",
  description:
    "Tests a high-salience first-order incentive treatment inspired by Instacart.",
};

const EXPERIENCE_CONTROL = 1;
const EXPERIENCE_TREATMENT = 2;

export default function InstacartStyleIncentiveBannerPage() {
  const [experience, setExperience] = useState(EXPERIENCE_TREATMENT);
  const [offerVisible, setOfferVisible] = useState(true);
  const [termsOpen, setTermsOpen] = useState(false);
  const [shoppingFeedback, setShoppingFeedback] = useState<string | null>(null);

  useEffect(() => {
    document.title = prototypeMeta.name;
  }, []);

  const resetExperience = (): void => {
    setOfferVisible(true);
    setTermsOpen(false);
    setShoppingFeedback(null);
  };

  const handleExperienceChange = (nextExperience: number): void => {
    setExperience(nextExperience);
    resetExperience();
  };

  const handleStartShopping = (): void => {
    setOfferVisible(false);
    setTermsOpen(false);
    setShoppingFeedback(
      "Your welcome offer is ready. These bestsellers all qualify for 50% off."
    );
    window.requestAnimationFrame(() => {
      document
        .getElementById("welcome-offer-products")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const isControl = experience === EXPERIENCE_CONTROL;

  return (
    <CompassProvider>
      <LiveToolbar
        hiddenOnMobile
        variantGroups={[
          {
            title: "Experience",
            queryKey: "experience",
            toggleMode: true,
            variants: [
              { variantNumber: EXPERIENCE_CONTROL, label: "Control modal" },
              {
                variantNumber: EXPERIENCE_TREATMENT,
                label: "Instacart banner",
              },
            ],
            active: experience,
            onChange: handleExperienceChange,
          },
        ]}
      />
      <RetailerLayout cartCount={4} hideFooter>
        <RetailerHomepage
          shoppingFeedback={shoppingFeedback}
          onDismissFeedback={() => {
            setShoppingFeedback(null);
          }}
        />

        {isControl ? (
          <ControlOfferModal
            open={offerVisible}
            onClose={() => {
              setOfferVisible(false);
            }}
            onStartShopping={handleStartShopping}
          />
        ) : offerVisible ? (
          <IncentiveTakeover
            onDismiss={() => {
              setOfferVisible(false);
            }}
            onOpenTerms={() => {
              setTermsOpen(true);
            }}
            onStartShopping={handleStartShopping}
          />
        ) : null}

        {!offerVisible ? (
          <div className="fixed right-(--slate-spacing-layout-edge-mobile) bottom-(--slate-spacing-layout-edge-mobile) z-30 md:right-(--slate-spacing-layout-edge-tablet) md:bottom-(--slate-spacing-layout-edge-tablet)">
            <Button variant="secondary" onClick={resetExperience}>
              Reopen $100 offer
            </Button>
          </div>
        ) : null}

        <TermsModal
          open={termsOpen}
          onClose={() => {
            setTermsOpen(false);
          }}
        />
      </RetailerLayout>
    </CompassProvider>
  );
}
